'use client'

import { Component, type ErrorInfo, type ReactNode } from 'react'
import type { PagebuilderSectionNames } from 'cms/constants/pagebuilder'

import { reportSentryError } from 'lib/sentry'

interface Props {
    children: ReactNode
    fallback?: ReactNode
    sectionType?: PagebuilderSectionNames
    sectionId?: string
}

interface State {
    hasError: boolean
}

export class SectionErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false }

    static getDerivedStateFromError(): State {
        return { hasError: true }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        reportSentryError(error, {
            tags: { error_type: 'section_render_error', section_type: this.props.sectionType || '' },
            extra: { componentStack: errorInfo.componentStack, sectionId: this.props.sectionId || '' }
        })
    }

    componentDidUpdate(prevProps: Props) {
        if (this.state.hasError && prevProps.children !== this.props.children) {
            this.setState({ hasError: false })
        }
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback ?? null
        }

        return this.props.children
    }
}
